import { useCallback, useEffect, useRef, useState } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { authMode, devSignIn, restoreSession, signInWithMicrosoft, type Session } from '@shared/auth';
import { createApi, type ApiClient } from '@shared/api';
import { connectHub, HubEvents, type ConnectionStatus } from '@shared/realtime';
import type { DisplayStateDto, DisplaySummaryDto, DrawingPresentationDto, DrawingStartedDto, MeDto, WinnerRevealedDto } from '@shared/types';
import { LeaderboardScreen } from './screens/Leaderboard';
import { DrawingReadyScreen } from './screens/DrawingReady';
import { SpinScreen } from './screens/Spin';
import { WinnerScreen } from './screens/Winner';
import { useKiosk } from './kiosk';

const DISPLAY_ID = import.meta.env.VITE_DISPLAY_ID || new URLSearchParams(location.search).get('display') || 'shop-main';
const VERSION = import.meta.env.VITE_APP_VERSION || 'dev';
const HEARTBEAT_MS = 30_000;

type View =
  | { mode: 'Leaderboard' }
  | { mode: 'DrawingReady'; drawing: DrawingPresentationDto }
  | { mode: 'Spinning'; started: DrawingStartedDto; receivedAtMs: number }
  | { mode: 'Winner'; winner: WinnerRevealedDto; receivedAtMs: number };

function viewFromState(s: DisplayStateDto): View {
  const now = Date.now();
  if (s.mode === 'Spinning' && s.started) return { mode: 'Spinning', started: s.started, receivedAtMs: now };
  if (s.mode === 'Winner' && s.winner) return { mode: 'Winner', winner: s.winner, receivedAtMs: now };
  if (s.mode === 'DrawingReady' && s.staged) return { mode: 'DrawingReady', drawing: s.staged };
  return { mode: 'Leaderboard' };
}

function viewKey(v: View): string {
  switch (v.mode) {
    case 'Spinning': return `spin:${v.started.drawing.drawingId}`;
    case 'Winner': return `win:${v.winner.drawingId}`;
    case 'DrawingReady': return `ready:${v.drawing.drawingId}`;
    default: return 'board';
  }
}

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    restoreSession().then(setSession).catch((e: Error) => { setAuthError(e.message); setSession(null); });
  }, []);

  if (session === undefined) return <Splash message="Starting…" />;
  if (!session) return <SignIn error={authError} onDev={() => setSession(devSignIn({ user: 'shop-display@kes-llc.com', name: 'Shop Display', roles: ['RewardsDisplay'] }, 'local'))} />;
  return <DisplayRuntime session={session} onSignedOut={() => setSession(null)} />;
}

function DisplayRuntime({ session, onSignedOut }: { session: Session; onSignedOut: () => void }) {
  const api = useRef<ApiClient>(createApi(session, onSignedOut)).current;
  const [roleOk, setRoleOk] = useState<boolean | null>(null);
  const [summary, setSummary] = useState<DisplaySummaryDto | null>(null);
  const [view, setView] = useState<View>({ mode: 'Leaderboard' });
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const hubRef = useRef<HubConnection | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;
  useKiosk();

  // Only accept a new view if it is a different screen; never restart an in-progress spin for the same drawing.
  const applyView = useCallback((next: View) => {
    if (viewKey(next) === viewKey(viewRef.current)) return;
    setView(next);
  }, []);

  const refreshSummary = useCallback(async () => {
    try { setSummary(await api.get<DisplaySummaryDto>(`/api/display/summary?displayId=${encodeURIComponent(DISPLAY_ID)}`)); } catch { /* keep last */ }
  }, [api]);

  useEffect(() => {
    api.get<MeDto>('/api/me')
      .then((me) => setRoleOk(me.roles.includes('RewardsDisplay') || me.roles.includes('RewardsAdmin')))
      .catch(() => setRoleOk(false));
  }, [api]);

  useEffect(() => {
    if (!roleOk) return;
    void refreshSummary();
    const poll = setInterval(refreshSummary, 60_000);
    let debounce: ReturnType<typeof setTimeout> | undefined;

    const conn = connectHub({
      session,
      displayId: DISPLAY_ID,
      onStatus: setStatus,
      onConnected: async (hub) => {
        hubRef.current = hub;
        try { applyView(viewFromState(await hub.invoke<DisplayStateDto>('GetState'))); } catch { /* next heartbeat */ }
        void refreshSummary();
      },
    });
    const hub = conn.hub;
    hub.on(HubEvents.LeaderboardUpdated, () => { clearTimeout(debounce); debounce = setTimeout(refreshSummary, 2_000); });
    hub.on(HubEvents.DrawingStaged, (d: DrawingPresentationDto) => applyView({ mode: 'DrawingReady', drawing: d }));
    hub.on(HubEvents.DrawingStarted, (s: DrawingStartedDto) => applyView({ mode: 'Spinning', started: s, receivedAtMs: Date.now() }));
    hub.on(HubEvents.WinnerRevealed, (w: WinnerRevealedDto) => applyView({ mode: 'Winner', winner: w, receivedAtMs: Date.now() }));
    hub.on(HubEvents.ReturnToLeaderboard, () => { applyView({ mode: 'Leaderboard' }); void refreshSummary(); });

    const beat = setInterval(async () => {
      if (hub.state !== 'Connected') return;
      const v = viewRef.current;
      const drawingId = v.mode === 'Spinning' ? v.started.drawing.drawingId : v.mode === 'Winner' ? v.winner.drawingId : v.mode === 'DrawingReady' ? v.drawing.drawingId : null;
      try {
        const s = await hub.invoke<DisplayStateDto>('Heartbeat', { displayId: DISPLAY_ID, mode: v.mode, drawingId, clientVersion: VERSION });
        applyView(viewFromState(s)); // self-heal if an event was missed
      } catch { /* reconnect logic handles it */ }
    }, HEARTBEAT_MS);

    const onError = (e: ErrorEvent) => { void hub.invoke('DisplayError', String(e.message).slice(0, 500)).catch(() => undefined); };
    window.addEventListener('error', onError);

    return () => {
      clearInterval(poll); clearInterval(beat); clearTimeout(debounce);
      window.removeEventListener('error', onError);
      void conn.stop();
    };
  }, [roleOk, session, applyView, refreshSummary]);

  const onSpinStart = useCallback((drawingId: string) => {
    void hubRef.current?.invoke('DrawingAnimationStarted', drawingId).catch(() => undefined);
  }, []);

  // Tell the server the animation finished. The server only reveals once its own clock agrees the spin is over,
  // so retry briefly; the server watchdog completes the drawing anyway if this display never reports back.
  const onSpinEnd = useCallback(async (drawingId: string) => {
    for (let attempt = 0; attempt < 20; attempt++) {
      if (viewRef.current.mode !== 'Spinning') return;
      try {
        const w = await hubRef.current?.invoke<WinnerRevealedDto | null>('DrawingAnimationCompleted', drawingId);
        if (w) { applyView({ mode: 'Winner', winner: w, receivedAtMs: Date.now() }); return; }
      } catch { /* retry */ }
      await new Promise((r) => setTimeout(r, 1_000));
    }
    try { if (hubRef.current) applyView(viewFromState(await hubRef.current.invoke<DisplayStateDto>('GetState'))); } catch { /* heartbeat heals */ }
  }, [applyView]);

  // Fallback if ReturnToLeaderboard is missed: re-read state shortly after the celebration should have ended.
  useEffect(() => {
    if (view.mode !== 'Winner') return;
    const ms = (view.winner.celebrationSeconds + 8) * 1000;
    const t = setTimeout(async () => {
      try { if (hubRef.current) { const s = await hubRef.current.invoke<DisplayStateDto>('GetState'); setView(viewFromState(s)); } } catch { /* ignore */ }
    }, ms);
    return () => clearTimeout(t);
  }, [view]);

  if (roleOk === null) return <Splash message="Checking display access…" />;
  if (!roleOk) return <Splash message="This account is not assigned the Rewards Display role. Ask an administrator to assign RewardsDisplay in Entra ID." />;

  return (
    <div className="stage">
      {view.mode === 'Leaderboard' && <LeaderboardScreen summary={summary} />}
      {view.mode === 'DrawingReady' && <DrawingReadyScreen drawing={view.drawing} />}
      {view.mode === 'Spinning' && (
        <SpinScreen key={view.started.drawing.drawingId} started={view.started} receivedAtMs={view.receivedAtMs}
          onSpinStart={() => onSpinStart(view.started.drawing.drawingId)} onSpinEnd={() => onSpinEnd(view.started.drawing.drawingId)} />
      )}
      {view.mode === 'Winner' && <WinnerScreen winner={view.winner} />}
      {status !== 'connected' && <div className="conn-badge" role="status">{status === 'reconnecting' || status === 'connecting' ? 'Reconnecting…' : 'Offline — retrying'}</div>}
    </div>
  );
}

function Splash({ message }: { message: string }) {
  return (
    <div className="stage splash">
      <img src="./brand/kes-logo-reversed.png" alt="Keystone Energy Services" className="splash-logo" />
      <p>{message}</p>
    </div>
  );
}

function SignIn({ error, onDev }: { error: string | null; onDev: () => void }) {
  return (
    <div className="stage splash">
      <img src="./brand/kes-logo-reversed.png" alt="Keystone Energy Services" className="splash-logo" />
      <h1>Safety Accountability Program</h1>
      <p>Shop display sign-in. Sign in with an admin account.</p>
      {authMode === 'entra'
        ? <button className="btn-signin" onClick={() => void signInWithMicrosoft().catch(() => undefined)}>Sign in with Microsoft</button>
        : <button className="btn-signin" onClick={onDev}>Sign in as shop display (local dev)</button>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
